import { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Target, ChevronLeft, ChevronRight, Clock, BookOpen, List } from 'lucide-react';
import { useDailyGoalHistory, type DailyGoalHistoryEntry } from '@/hooks/useDailyGoalHistory';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameMonth, 
  isSameDay,
  addMonths,
  subMonths,
  getDay,
  isToday,
  isFuture
} from 'date-fns';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface DailyGoalCalendarProps {
  year: number;
}

export function DailyGoalCalendar({ year }: DailyGoalCalendarProps) {
  const [currentMonth, setCurrentMonth] = useState(new Date(year, new Date().getMonth()));
  
  const { data: historyData, isLoading } = useDailyGoalHistory({
    year: currentMonth.getFullYear(),
    month: currentMonth.getMonth() + 1,
  });

  // Create a map for quick lookup
  const historyMap = useMemo(() => {
    const map = new Map<string, DailyGoalHistoryEntry>();
    historyData?.forEach(entry => map.set(entry.date, entry));
    return map;
  }, [historyData]);

  // Calculate stats for the month
  const monthStats = useMemo(() => {
    if (!historyData || historyData.length === 0) {
      return { completed: 0, total: 0, streak: 0 };
    }

    const completed = historyData.filter(d => d.isComplete).length;
    const total = historyData.length;

    // Calculate current streak (consecutive completed days ending at most recent)
    let streak = 0;
    const sortedData = [...historyData].sort((a, b) => b.date.localeCompare(a.date));
    for (const entry of sortedData) {
      if (entry.isComplete) {
        streak++;
      } else {
        break;
      }
    }

    return { completed, total, streak };
  }, [historyData]);

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const days = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Add padding days for the start of the week
    const startPadding = getDay(monthStart); // 0 = Sunday
    const paddingDays = Array(startPadding).fill(null);
    
    return [...paddingDays, ...days];
  }, [currentMonth]);

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    const next = addMonths(currentMonth, 1);
    // Don't go beyond current month
    if (next <= new Date()) {
      setCurrentMonth(next);
    }
  };

  const getGoalTypeIcon = (type: 'time' | 'pages' | 'chapters') => {
    switch (type) {
      case 'time':
        return <Clock className="w-3 h-3" />;
      case 'pages':
        return <BookOpen className="w-3 h-3" />;
      case 'chapters':
        return <List className="w-3 h-3" />;
    }
  };

  const getGoalTypeUnit = (type: 'time' | 'pages' | 'chapters') => {
    switch (type) {
      case 'time':
        return 'min';
      case 'pages':
        return 'pages';
      case 'chapters':
        return 'chapters';
    }
  };

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Card className="border-border/50">
      <CardContent className="p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 text-sm font-medium">
              <Target className="w-3.5 h-3.5 text-primary" />
              Goals
            </span>
            <span className="text-xs text-muted-foreground">
              {monthStats.completed}/{monthStats.total}
            </span>
            {monthStats.streak > 0 && (
              <span className="text-xs text-primary font-medium">🔥{monthStats.streak}</span>
            )}
          </div>
          
          {/* Month navigation */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              onClick={goToPreviousMonth}
              className="h-6 w-6"
            >
              <ChevronLeft className="h-3 w-3" />
            </Button>
            <span className="text-[10px] font-medium min-w-[50px] text-center text-muted-foreground">
              {format(currentMonth, 'MMM')}
            </span>
            <Button
              variant="ghost"
              size="icon"
              onClick={goToNextMonth}
              disabled={isSameMonth(currentMonth, new Date())}
              className="h-6 w-6"
            >
              <ChevronRight className="h-3 w-3" />
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="h-8 flex items-center">
            <div className="animate-pulse text-muted-foreground text-xs">Loading...</div>
          </div>
        ) : (
          <TooltipProvider delayDuration={100}>
            <div>
              {/* Weekday labels */}
              <div className="flex gap-[3px] mb-1">
                {weekDays.map(day => (
                  <div key={day} className="w-3 text-[8px] text-muted-foreground text-center">
                    {day.charAt(0)}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-[3px]">
              {calendarDays.map((day, index) => {
                if (!day) {
                  return <div key={`empty-${index}`} className="w-3 h-3" />;
                }

                const dateStr = format(day, 'yyyy-MM-dd');
                const entry = historyMap.get(dateStr);
                const isFutureDay = isFuture(day);
                const isCurrentDay = isToday(day);

                return (
                  <Tooltip key={dateStr}>
                    <TooltipTrigger asChild>
                      <div
                        className={cn(
                          "w-3 h-3 rounded-[2px] transition-colors cursor-default",
                          isFutureDay && "opacity-20",
                          isCurrentDay && "ring-1 ring-primary",
                          !isFutureDay && !entry && "bg-muted/40",
                          entry?.isComplete && "bg-primary",
                          entry && !entry.isComplete && "bg-destructive/30"
                        )}
                      />
                    </TooltipTrigger>
                    {entry && !isFutureDay && (
                      <TooltipContent side="top" className="text-xs py-1.5 px-2">
                        <div className="space-y-0.5">
                          <div className="font-medium">{format(day, 'MMM d')}</div>
                          <div className="flex items-center gap-1 text-muted-foreground">
                            {getGoalTypeIcon(entry.type)}
                            <span>{entry.current}/{entry.target}</span>
                          </div>
                        </div>
                      </TooltipContent>
                    )}
                  </Tooltip>
                );
              })}
              </div>
            </div>
          </TooltipProvider>
        )}
      </CardContent>
    </Card>
  );
}
